<template>
  <div class="card modal-wrapper" id="productLineEditorCard">
    <div class="card-header">
      <h5 class="card-title text-center">Product Line Editor</h5>
      <hr>
    </div>

    <div class="card-body">
      <div class="row">
        <div class="col-6">
          <BasicTextInput
            type="number"
            label="Quantity"
            class="mb-1"
            v-model="formData.quantity"
            placeholder="Leave blank to keep current values"
            :min="0"
            :step="1"
          />

          <PercentInputAllowNegative
            label="Discount A"
            class="mb-1"
            v-model="formData.discountA"
            placeholder="Leave blank to keep current values"
            :disabled="hasGrossProfitValue"
          />

          <PercentInputAllowNegative
            label="Discount B"
            class="mb-1"
            v-model="formData.discountB"
            placeholder="Leave blank to keep current values"
            :disabled="hasGrossProfitValue"
          />
        </div>

        <div class="col-6">
          <PercentInput
            label="Modify Gross Profit"
            class="mb-1"
            v-model="formData.grossProfit"
            :disabled="hasCustomerCostValue || hasDealerCostValue || hasDiscountValue"
          />

          <PercentInput
            label="Modify Customer Cost"
            class="mb-1"
            v-model="formData.customerCost"
            placeholder="Leave blank to keep current values"
            :disabled="hasGrossProfitValue"
          />

          <PercentInput
            label="Modify Dealer Cost"
            class="mb-1"
            v-model="formData.dealerCost"
            placeholder="Leave blank to keep current values"
            :disabled="hasGrossProfitValue"
          />
        </div>
      </div>

      <div class="entries-notice">
        <p class="text-center text-muted">
          <em>Entries here will be applied to ALL checked product lines</em>
        </p>
      </div>

      <div class="btn-wrapper center">
        <button class="btn btn-secondary mr-2" @click="cancel" :disabled="isProcessing">
          Cancel
        </button>
        <button
          class="btn btn-primary"
          @click="applyChanges"
          :disabled="isProcessing || !hasAnyChanges"
        >
          <span v-if="isProcessing" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
          {{ isProcessing ? 'Processing...' : 'Apply Changes' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PercentInputAllowNegative from '@/components/inputComponents/PercentInputAllowNegative.vue';
import PercentInput from '@/components/inputComponents/PercentInput.vue';

export default {
  name: "ProductLineEditor",
  components: {
    PercentInputAllowNegative,
    PercentInput
  },
  props: {
    selectedRows: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isProcessing: false,
      formData: {
        quantity: null,
        discountA: null,
        discountB: null,
        grossProfit: null,
        customerCost: null,
        dealerCost: null
      }
    }
  },
  computed: {
    hasAnyChanges() {
      return Object.values(this.formData).some(value =>
        value !== null && value !== undefined && value !== ''
      );
    },
    hasGrossProfitValue() {
      const val = this.formData.grossProfit;
      return val !== null && val !== undefined && val !== '';
    },
    hasCustomerCostValue() {
      const val = this.formData.customerCost;
      return val !== null && val !== undefined && val !== '';
    },
    hasDealerCostValue() {
      const val = this.formData.dealerCost;
      return val !== null && val !== undefined && val !== '';
    },
    hasDiscountValue() {
      const discountA = this.formData.discountA;
      const discountB = this.formData.discountB;
      return (discountA !== null && discountA !== undefined && discountA !== '') ||
             (discountB !== null && discountB !== undefined && discountB !== '');
    }
  },
  methods: {
    applyChanges() {
      if (!this.hasAnyChanges) {
        return;
      }

      // Validate quantity is a positive whole number (or 0)
      if (this.formData.quantity !== null && this.formData.quantity !== undefined && this.formData.quantity !== '') {
        const qty = Number(this.formData.quantity);
        if (qty < 0 || !Number.isInteger(qty)) {
          this.$store.dispatch('ADD_NOTIFICATION', {
            text: 'Quantity must be a positive whole number (or 0)',
            type: 'error'
          });
          this.isProcessing = false;
          return;
        }
      }

      // Validate that both Gross Profit and Customer Cost are not set at the same time
      if (this.hasGrossProfitValue && this.hasCustomerCostValue) {
        this.$store.dispatch('ADD_NOTIFICATION', {
          text: 'Cannot set both Modify Gross Profit and Modify Customer Cost at the same time',
          type: 'error'
        });
        this.isProcessing = false;
        return;
      }

      // Validate that both Gross Profit and Dealer Cost are not set at the same time
      if (this.hasGrossProfitValue && this.hasDealerCostValue) {
        this.$store.dispatch('ADD_NOTIFICATION', {
          text: 'Cannot set both Modify Gross Profit and Modify Dealer Cost at the same time',
          type: 'error'
        });
        this.isProcessing = false;
        return;
      }

      this.isProcessing = true;

      // Create changes object with only non-empty values
      const changes = {};
      Object.keys(this.formData).forEach(key => {
        const value = this.formData[key];
        if (value !== null && value !== undefined && value !== '') {
          changes[key] = value;
        }
      });

      // Emit the changes to parent component
      this.$emit('applyBulkChanges', changes);
    },

    cancel() {
      this.resetForm();
      this.$emit('cancel');
    },

    resetForm() {
      this.formData = {
        quantity: null,
        discountA: null,
        discountB: null,
        grossProfit: null,
        customerCost: null,
        dealerCost: null
      };
      this.isProcessing = false;
    },

    finishProcessing() {
      this.isProcessing = false;
      this.resetForm();
    },

    handleKeydown(event) {
      // Check if Enter key was pressed (key code 13)
      if (event.key === 'Enter' || event.keyCode === 13) {
        // Prevent default form submission behavior
        event.preventDefault();
        // Only apply if there are changes and not currently processing
        if (this.hasAnyChanges && !this.isProcessing) {
          this.applyChanges();
        }
      }
    }
  },

  mounted() {
    // Add keydown event listener to the component
    this.$el.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    // Clean up event listener when component is destroyed
    this.$el.removeEventListener('keydown', this.handleKeydown);
  },
  
  watch: {
    selectedRows: {
      handler(newRows) {
        if (newRows && newRows.length === 0) {
          this.resetForm();
        }
      },
      immediate: true
    },
    hasGrossProfitValue(newVal) {
      // When gross profit gets a value, clear customer cost, dealer cost, and discounts
      if (newVal) {
        if (this.hasCustomerCostValue) {
          this.formData.customerCost = null;
        }
        if (this.hasDealerCostValue) {
          this.formData.dealerCost = null;
        }
        if (this.hasDiscountValue) {
          this.formData.discountA = null;
          this.formData.discountB = null;
        }
      }
    },
    hasCustomerCostValue(newVal) {
      // When customer cost gets a value, clear gross profit
      if (newVal && this.hasGrossProfitValue) {
        this.formData.grossProfit = null;
      }
    },
    hasDealerCostValue(newVal) {
      // When dealer cost gets a value, clear gross profit
      if (newVal && this.hasGrossProfitValue) {
        this.formData.grossProfit = null;
      }
    },
    hasDiscountValue(newVal) {
      // When discount gets a value, clear gross profit
      if (newVal && this.hasGrossProfitValue) {
        this.formData.grossProfit = null;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.card {
  max-height: 90vh;
  overflow: hidden;
}

#productLineEditorCard {
  min-width: 480px;
  max-width: 550px;
  width: 50vw;
  min-height: 280px;
  max-height: 350px;
}

.card-body {
  width: 100%;
  padding-top: 8px;
}

.card-header {
  hr {
    margin-bottom: 8px;
    margin-top: 8px;
  }
}

.entries-notice {
  margin-top: 8px;
  padding: 6px 8px;
  background-color: #f8f9fa;
  border-radius: 3px;
}

.modal-wrapper {
  min-width: 480px;
  display: flex;
  justify-content: space-around;
  align-items: center;

  .card-header {
    min-width: 100%;
    padding-bottom: 2px;
  }

  .btn-wrapper {
    margin-top: 10px;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;

    .btn {
      min-width: 90px;
      font-size: 12px;
      padding: 5px 10px;
    }

    &.center {
      justify-content: space-around;
      align-items: center;
    }
  }
}

// Smaller font styling for input fields
:deep(.text-input) {
  label {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 2px;
  }

  .form-control {
    font-size: 12px;
    padding: 4px 8px;
    height: auto;
    line-height: 1.3;
  }
}

// Smaller notice text
.entries-notice {
  p {
    font-size: 11px;
    margin-bottom: 0;
    line-height: 1.3;
  }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
